import React, { memo } from 'react';
import { get } from 'lodash-es';
import PropTypes from 'prop-types';
import { InventoryEntities } from 'client/data/models/inventory';
import { VdpModule } from 'site-modules/shared/components/inventory/vdp-module/vdp-module';
import { WarrantyPeriodProgress } from 'site-modules/shared/components/inventory/warranty-module/warranty-period-progress/warranty-period-progress';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { getCurrentDate } from 'site-modules/shared/utils/date-utils';
import { WarrantyDetailedComments } from 'site-modules/shared/components/inventory/warranty-module/warranty-item/warranty-detailed-comments/warranty-detailed-comments';
import {
  getWarrantyMilesLeft,
  getWarrantyYearsLeft,
  warrantyConditionsUsedDetailedCopyProviders,
} from 'site-modules/shared/utils/inventory/warranty';
import { WarrantyConditionsBox } from 'site-modules/shared/components/inventory/warranty-module/warranty-item/warranty-item-used-detailed/warranty-conditions-box/warranty-conditions-box';
import { WarrantyConditionsLine } from 'site-modules/shared/components/inventory/warranty-module/warranty-item/warranty-item-used-detailed/warranty-conditions-line/warranty-conditions-line';
import { ViewMoreAccordionItem } from 'site-modules/shared/components/inventory/accordion/view-more-accordion-item/view-more-accordion-item';

import './warranty-item-used-detailed.scss';

export const WarrantyItemUsedDetailed = memo(({ warrantyItem, vinData, animateWarrantyPeriodProgress }) => {
  const currentMiles = get(vinData, DATA_PATH.MILEAGE, 0);
  const currentYear = get(vinData, DATA_PATH.YEAR, 0);
  const yearsOld = getCurrentDate().getFullYear() - currentYear;
  const milesLeft = getWarrantyMilesLeft(warrantyItem, vinData);
  const yearsLeft = getWarrantyYearsLeft(warrantyItem, vinData);
  const maxMiles = milesLeft === Number.MAX_SAFE_INTEGER ? Number.MAX_SAFE_INTEGER : Number(warrantyItem.maxMileage);
  const maxYears = yearsLeft === Number.MAX_SAFE_INTEGER ? Number.MAX_SAFE_INTEGER : Number(warrantyItem.maxYears);

  return (
    <VdpModule
      className="warranty-item-used-detailed bg-white"
      titleClassName="heading-4 mb-0"
      sectionTitle={warrantyItem.warrantyType}
      headerTag="h3"
    >
      <div className="d-flex align-items-center">
        <div className="warranty-item-used-detailed-description">
          <WarrantyConditionsBox>
            <h4 className="size-14 fw-medium mb-0_75 text-cool-gray-40">Warranty Remaining*</h4>
            <WarrantyConditionsLine color="bg-turquoise-30">
              {warrantyConditionsUsedDetailedCopyProviders.getYearsCopy(vinData, warrantyItem)}
            </WarrantyConditionsLine>
            <WarrantyConditionsLine color="bg-blue-40" className="mt-0_75">
              {warrantyConditionsUsedDetailedCopyProviders.getMilesCopy(vinData, warrantyItem)}
            </WarrantyConditionsLine>
          </WarrantyConditionsBox>
        </div>
        <div className="warranty-item-used-detailed-progress ms-1">
          <WarrantyPeriodProgress
            totalMiles={maxMiles}
            currentMiles={currentMiles}
            totalYears={maxYears}
            currentYears={yearsOld}
            animateWarrantyPeriodProgress={animateWarrantyPeriodProgress}
          />
        </div>
      </div>
      <ViewMoreAccordionItem
        id={warrantyItem.id}
        className="mt-1_5"
        collapsedSummary="Details"
        expandedSummary="Hide Details"
        trackingId="view_details"
        trackingValue={warrantyItem.warrantyType}
      >
        <WarrantyDetailedComments warrantyItem={warrantyItem} />
      </ViewMoreAccordionItem>
    </VdpModule>
  );
});

WarrantyItemUsedDetailed.propTypes = {
  warrantyItem: InventoryEntities.WarrantyItem.isRequired,
  vinData: InventoryEntities.InventoryVin.isRequired,
  animateWarrantyPeriodProgress: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  className: PropTypes.string,
};

WarrantyItemUsedDetailed.displayName = 'WarrantyItemUsedDetailed';
